import React, { useEffect, useState } from "react";
import { Box } from "@chakra-ui/react";
import { useColorMode } from "@chakra-ui/react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { getThemeClasses } from "../../utils/themeUtils";
import { FiShield } from "react-icons/fi";

interface User {
  id: string;
  role?: string;
  permissions?: string;
}

interface SystemSettingsProps {
  currentUser: User | null;
  organizationId: string;
}

const SystemSettings: React.FC<SystemSettingsProps> = ({ currentUser, organizationId }) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  const isAdminUser =
    currentUser?.role?.toLowerCase() === "admin" ||
    currentUser?.permissions?.toLowerCase() === "admin";

  // ── MFA state ──────────────────────────────────────────────────────────────
  const [isMfaEnabled, setIsMfaEnabled] = useState<boolean>(true);
  const [mfaToggling, setMfaToggling] = useState(false);

  useEffect(() => {
    if (!organizationId) return;
    const fetchMfa = async () => {
      try {
        const orgRef = doc(db, "organization", organizationId);
        const snap = await getDoc(orgRef);
        if (snap.exists()) {
          const data = snap.data();
          setIsMfaEnabled(data.isMfaEnabled !== false);
        }
      } catch (err) {
        console.error("Error fetching MFA setting:", err);
      }
    };
    fetchMfa();
  }, [organizationId]);

  const handleMfaToggle = async () => {
    if (!organizationId || mfaToggling) return;
    setMfaToggling(true);
    const newValue = !isMfaEnabled;
    try {
      const orgRef = doc(db, "organization", organizationId);
      await updateDoc(orgRef, { isMfaEnabled: newValue });
      setIsMfaEnabled(newValue);
    } catch (err) {
      console.error("Error updating MFA setting:", err);
    } finally {
      setMfaToggling(false);
    }
  };

  // ── Shared toggle button ────────────────────────────────────────────────────
  const Toggle = ({
    enabled,
    onToggle,
    disabled = false,
    id,
  }: {
    enabled: boolean;
    onToggle: () => void;
    disabled?: boolean;
    id: string;
  }) => (
    <button
      id={id}
      type="button"
      aria-label="toggle"
      onClick={onToggle}
      disabled={disabled}
      className={`relative inline-flex h-7 w-13 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#B59143] focus:ring-offset-2 disabled:opacity-50 ${
        enabled
          ? "bg-[#B59143]"
          : isDark
          ? "bg-[#2E2F33]"
          : "bg-gray-300"
      }`}
      style={{ width: 48 }}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          enabled ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );

  // ── Row component ───────────────────────────────────────────────────────────
  const SettingRow = ({
    icon,
    title,
    subtitle,
    right,
  }: {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    right: React.ReactNode;
  }) => (
    <div
      className={`flex items-center justify-between py-4 transition-all ${
        isDark ? "hover:bg-white/5" : "hover:bg-black/5"
      } rounded-lg px-2`}
    >
      <div className="flex items-center gap-4">
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isDark ? "bg-[#1A1C1F]" : "bg-gray-100"
          }`}
        >
          {icon}
        </div>
        <div>
          <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
            {title}
          </p>
          <p className={`text-xs mt-0.5 ${theme.textMuted}`}>{subtitle}</p>
        </div>
      </div>
      <div>{right}</div>
    </div>
  );

  return (
    <Box>
      {/* ── Standard module header ────────────────────────────────────────── */}
      <div className={`flex justify-between items-center p-4 ${theme.headerGradient} rounded-md mb-4 border ${theme.border}`}>
        {isDark && (
          <>
            <span className={theme.headerOverlayGlow} />
            <span className={theme.headerOverlayTopLine} />
            <span className={theme.headerOverlayBottomGlow} />
            <span className={theme.headerOverlaySweep} />
          </>
        )}
        <div className="flex flex-col space-y-1">
          <span className={`text-2xl font-bold ${theme.text}`}>
            System Settings
          </span>
          <span className={`text-xs ${theme.textMuted}`}>
            Manage security and organizational preferences.
          </span>
        </div>
      </div>

      {/* ── Settings content ─────────────────────────────────────────────── */}
      <div className="mt-6">
        {/* Security (admin only) */}
        {isAdminUser ? (
          <div className="mt-4">
            <div className="px-2 mb-2">
              <p className={`text-xs font-bold uppercase tracking-widest ${isDark ? "text-[#B59143]" : "text-[#B59143]"}`}>
                Security
              </p>
            </div>
            <div className="space-y-1">
              <SettingRow
                icon={React.createElement(FiShield as any, { size: 18, style: { color: "#B59143" } })}
                title="Multi-Factor Authentication"
                subtitle={
                  isMfaEnabled
                    ? "OTP verification is required for all users at sign-in."
                    : "OTP verification is disabled for all users in this organisation."
                }
                right={
                  <Toggle
                    id="system-settings-mfa"
                    enabled={isMfaEnabled}
                    onToggle={handleMfaToggle}
                    disabled={mfaToggling}
                  />
                }
              />
            </div>
          </div>
        ) : (
          <div className="text-center py-20">
            <p className={`${theme.textMuted} text-sm`}>
              You do not have permission to manage system-wide settings.
            </p>
          </div>
        )}
      </div>
    </Box>
  );
};

export default SystemSettings;
